/**
 * Embedded Signup v5 do WhatsApp (Cloud API direta da Meta), do lado do navegador.
 *
 * Fontes (developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup): `implementation` (SDK,
 * `FB.init`, `FB.login`, evento `message`) e `version-5` (`extras: { version: 'v5' }` e o session info). O navegador só
 * junta o `code` (vale 30 segundos) e o session info; quem troca o code por token é o servidor.
 */

export const WA_EMBEDDED_SIGNUP_TYPE = 'WA_EMBEDDED_SIGNUP';

/** Endereço do SDK como na documentação da Meta (locale en_US). */
export const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

/** O app, a configuração de login e a versão da Graph API vêm do servidor (nada disso é segredo). */
export interface WhatsAppSignupConfig {
  appId: string;
  configId: string;
  graphApiVersion: string;
}

export interface FacebookLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

export interface FacebookSdk {
  init: (options: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }) => void;
  login: (callback: (response: FacebookLoginResponse) => void, options: Record<string, unknown>) => void;
}

type SdkWindow = Window & { FB?: FacebookSdk; fbAsyncInit?: () => void };

/**
 * O evento só vale vindo da Meta. A página oficial confere `endsWith('facebook.com')`, que deixaria passar
 * `evilfacebook.com`; aqui o endereço tem de ser https e o host, `facebook.com` ou um subdomínio dele.
 */
export const isFacebookOrigin = (origin: string): boolean => {
  try {
    const url = new URL(origin);
    return url.protocol === 'https:' && (url.hostname === 'facebook.com' || url.hostname.endsWith('.facebook.com'));
  } catch {
    return false;
  }
};

export type SignupMessage =
  | { kind: 'finish'; event: string; raw: Record<string, unknown> }
  | { kind: 'no_number'; event: string }
  | { kind: 'cancel'; step?: string }
  | { kind: 'error'; message?: string; errorCode?: string; sessionId?: string };

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const asText = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined);

/** Lê o evento `message` do Embedded Signup. `null` = não é para nós (origem de fora, outro type, não é JSON). */
export const parseSignupMessage = (event: { origin: string; data: unknown }): SignupMessage | null => {
  if (!isFacebookOrigin(event.origin)) return null;
  let payload: unknown = event.data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  const msg = asRecord(payload);
  if (!msg || msg.type !== WA_EMBEDDED_SIGNUP_TYPE) return null;
  const data = asRecord(msg.data) ?? {};
  switch (msg.event) {
    case 'FINISH':
    case 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING':
      return { kind: 'finish', event: msg.event, raw: msg };
    case 'FINISH_ONLY_WABA':
    case 'FINISH_OBO_MIGRATION':
    case 'FINISH_GRANT_ONLY_API_ACCESS':
      return { kind: 'no_number', event: msg.event };
    case 'CANCEL':
      // Cancelamento com error_message é erro que o cliente relatou na própria tela da Meta.
      if (data.error_message || data.error_code) {
        return {
          kind: 'error',
          message: asText(data.error_message),
          errorCode: asText(data.error_code),
          sessionId: asText(data.session_id),
        };
      }
      return { kind: 'cancel', step: asText(data.current_step) };
    case 'ERROR':
      return { kind: 'error', message: asText(data.error_message), errorCode: asText(data.error_code), sessionId: asText(data.session_id) };
    default:
      return null;
  }
};

const LOAD_ERROR = 'Não foi possível carregar o login da Meta. Confira a conexão com a internet e tente de novo.';

let sdkPromise: Promise<FacebookSdk> | null = null;
/** O SDK já carregado e iniciado: permite abrir o login dentro do clique, sem esperar. */
let readySdk: FacebookSdk | null = null;

export const resetFacebookSdkForTests = () => {
  sdkPromise = null;
  readySdk = null;
};

/** Carrega o SDK da Meta uma vez e o inicia (`FB.init`) com o app e a versão da Graph API da configuração. */
export const loadFacebookSdk = (config: WhatsAppSignupConfig, options: { timeoutMs?: number } = {}): Promise<FacebookSdk> => {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<FacebookSdk>((resolve, reject) => {
    const win = window as SdkWindow;
    const timer = window.setTimeout(() => {
      sdkPromise = null;
      reject(new Error(LOAD_ERROR));
    }, options.timeoutMs ?? 15000);
    win.fbAsyncInit = () => {
      window.clearTimeout(timer);
      const fb = win.FB;
      if (!fb) {
        sdkPromise = null;
        reject(new Error(LOAD_ERROR));
        return;
      }
      fb.init({ appId: config.appId, autoLogAppEvents: true, xfbml: true, version: config.graphApiVersion });
      readySdk = fb;
      resolve(fb);
    };
    if (!document.querySelector(`script[src="${FACEBOOK_SDK_URL}"]`)) {
      const script = document.createElement('script');
      script.src = FACEBOOK_SDK_URL;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        window.clearTimeout(timer);
        sdkPromise = null;
        script.remove();
        reject(new Error(LOAD_ERROR));
      };
      document.head.appendChild(script);
    }
  });
  return sdkPromise;
};

export type SignupOutcome =
  | { kind: 'finished'; code: string; sessionInfo: Record<string, unknown> }
  | { kind: 'cancelled'; step?: string }
  | { kind: 'error'; message: string; errorCode?: string; sessionId?: string };

const NO_NUMBER_MESSAGE =
  'O cadastro terminou sem um número de WhatsApp. Escolha ou cadastre um número e tente de novo.';
const NO_SESSION_MESSAGE =
  'Não recebemos os dados do número da Meta. Conclua o cadastro do WhatsApp até o fim e tente de novo.';

export interface LaunchOptions {
  /** SDK já carregado (testes ou pré-carga da tela). Sem ele, usa o carregado ou carrega. */
  sdk?: FacebookSdk;
  /** Depois do code, quanto esperar pelo session info (o code vale 30 s). */
  sessionInfoTimeoutMs?: number;
  /** Depois de o popup fechar sem code, quanto esperar por um CANCEL/ERROR da Meta antes de contar como cancelamento. */
  graceMs?: number;
}

/**
 * Abre o Embedded Signup. O `FB.login` é chamado de forma síncrona quando o SDK já está pronto (precisa ser dentro do clique,
 * senão o navegador bloqueia o popup): por isso a tela pré-carrega o SDK. Termina quando há o `code` do login e o session
 * info do evento `message`, ou em cancelamento ou erro.
 */
export const launchEmbeddedSignup = (config: WhatsAppSignupConfig, options: LaunchOptions = {}): Promise<SignupOutcome> =>
  new Promise<SignupOutcome>(resolve => {
    const sessionInfoTimeoutMs = options.sessionInfoTimeoutMs ?? 8000;
    const graceMs = options.graceMs ?? 600;
    let message: SignupMessage | null = null;
    let code: string | null = null;
    let loginAnswered = false;
    let done = false;
    let timer: number | null = null;

    const clearTimer = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const listener = (ev: MessageEvent) => {
      const parsed = parseSignupMessage({ origin: ev.origin, data: ev.data });
      if (!parsed) return;
      // O primeiro desfecho (cancelamento, erro ou fim) vale: um evento tardio não o sobrescreve.
      if (message === null) message = parsed;
      evaluate();
    };
    const finish = (outcome: SignupOutcome) => {
      if (done) return;
      done = true;
      clearTimer();
      window.removeEventListener('message', listener);
      resolve(outcome);
    };
    const waitThen = (ms: number, then: () => void) => {
      clearTimer();
      timer = window.setTimeout(then, ms);
    };

    const evaluate = () => {
      if (done) return;
      const current = message;
      if (current?.kind === 'cancel') return finish({ kind: 'cancelled', step: current.step });
      if (current?.kind === 'error')
        return finish({
          kind: 'error',
          message: current.message ?? 'A Meta não conseguiu concluir o cadastro do WhatsApp. Tente de novo.',
          errorCode: current.errorCode,
          sessionId: current.sessionId,
        });
      if (current?.kind === 'no_number') return finish({ kind: 'error', message: NO_NUMBER_MESSAGE });
      if (current?.kind === 'finish') {
        if (code) return finish({ kind: 'finished', code, sessionInfo: current.raw });
        return; // falta o code do callback do login
      }
      if (loginAnswered && !code) {
        // Popup fechado sem code: pode ainda chegar um CANCEL/ERROR com o motivo; passado o prazo, é cancelamento.
        waitThen(graceMs, () => finish({ kind: 'cancelled' }));
        return;
      }
      if (loginAnswered && code) {
        // Só o code: sem o session info não há como saber o número, e o code vence em 30 s.
        waitThen(sessionInfoTimeoutMs, () => finish({ kind: 'error', message: NO_SESSION_MESSAGE }));
      }
    };

    const startLogin = (sdk: FacebookSdk) => {
      window.addEventListener('message', listener);
      sdk.login(
        response => {
          loginAnswered = true;
          code = response?.authResponse?.code ?? null;
          evaluate();
        },
        {
          config_id: config.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: { version: 'v5' },
        },
      );
    };

    const ready = options.sdk ?? readySdk;
    if (ready) {
      startLogin(ready);
      return;
    }
    loadFacebookSdk(config).then(startLogin, (e: unknown) =>
      finish({ kind: 'error', message: e instanceof Error ? e.message : LOAD_ERROR }),
    );
  });
